import { Profile, User } from '@map/types';
import { useTheme } from '@mui/material/styles';
import { ProjectPortalUser } from '@pmp/projectPortalTypes';
import { useCurrentLanguage } from '@shared/hooks/intl';
import useMountEffect from '@shared/hooks/useMountEffect';
import { getConfigString } from '@shared/util/env';
import { isRtlLocale } from '@shared/util/locale';
import axios from 'axios';
import { DEV } from 'esm-env';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  API_URL,
  BASE_URL,
  addEventListener,
  executeCommand,
  getCurrentWidgetScreen,
  removeEventListener,
} from './setup';
import { HelpCenterArticle } from './types';

const LOCALE_MAP: Record<string, string> = {
  en: 'en-us',
  ar: 'ar',
  fr: 'fr',
};

// Map of languages that we actually have articles for with matching zendesk locales
const ARTICLE_LOCALE_MAP: Record<string, string> = {
  en: 'en-us',
  es: 'es',
} as const;

const showWidget = (keywords?: string[]) => {
  executeCommand('show');
  executeCommand('open');
  if (keywords) {
    executeCommand('helpCenter:setSuggestions', { labels: keywords });
  }
};
const hideWidget = () => {
  executeCommand('close');
  executeCommand('hide');
};

const useZendeskPageTracking = () => {
  const history = useHistory();
  useMountEffect(() => history.listen(() => executeCommand('updatePath', {})));
};

export const zendeskEnabled = !!getConfigString('zendesk-id');

export const useZendeskApi = () => {
  return useMemo(
    () => ({ on: addEventListener, off: removeEventListener, showWidget, hideWidget }),
    [],
  );
};

export const useZendesk = (user: User | ProjectPortalUser, profile?: Profile) => {
  useZendeskPageTracking();
  const theme = useTheme();
  useEffect(() => {
    if (!user.id) {
      return;
    }
    const locale = LOCALE_MAP[user.locale] || `${user.locale}-${user.locale}`;
    executeCommand('setLocale', locale);

    if (isRtlLocale(locale)) {
      executeCommand('updateSettings', {
        webWidget: {
          position: { horizontal: 'left' },
        },
      });
    }

    const nameFields = profile ? profile : (user as ProjectPortalUser);
    const name = `${nameFields.first_name} ${nameFields.last_name}`;
    executeCommand('identify', {
      name,
      email: user.email,
    });
    executeCommand('prefill', {
      name: {
        value: name,
      },
      email: {
        value: user.email,
      },
    });
  }, [user, profile]);
  useEffect(() => {
    const category = getConfigString('zendesk-category');
    executeCommand('updateSettings', {
      webWidget: {
        color: { theme: theme.palette.primary.main },
        helpCenter: { filter: { category } },
      },
    });
  }, [theme]);
};

export const useZendeskChatWidget = (suggestions?: string[]) => {
  useEffect(() => {
    executeCommand('updateSettings', {
      webWidget: {
        offset: {
          horizontal: '20px',
          vertical: '10px',
          mobile: {
            horizontal: '4px',
            vertical: '4px',
          },
        },
      },
    });
    if (suggestions) {
      executeCommand('helpCenter:setSuggestions', { labels: suggestions });
    }
  }, [suggestions]);
};

// this is needed since the hook will probably be recreated multiple times, losing the state
let globalUnreadMessageCount = 0;
export const useChatUnreadMessages = () => {
  const [unreadMessageCount, setUnreadMessageCount] = useState(globalUnreadMessageCount);
  useEffect(() => {
    const clearUnread = () => {
      globalUnreadMessageCount = 0;
      setUnreadMessageCount(0);
    };
    const updateUnread = (count: number) => {
      if (getCurrentWidgetScreen() === 'chat') {
        clearUnread();
        return;
      }
      globalUnreadMessageCount = count;
      setUnreadMessageCount(count);
    };
    addEventListener('chat:unreadMessages', updateUnread);
    addEventListener('open', clearUnread);
    if (DEV) {
      (window as any).debugTriggerHelpChat = () =>
        setUnreadMessageCount((globalUnreadMessageCount += 5));
    }
    return () => {
      removeEventListener('open', clearUnread);
      removeEventListener('chat:unreadMessages', updateUnread);
    };
  }, []);
  return unreadMessageCount;
};

export const useZendeskArticles = (labels: string[] | null) => {
  const [articles, setArticles] = useState<Array<HelpCenterArticle | undefined>>([]);
  const [status, setStatus] = useState({ loading: true });
  const language = useCurrentLanguage();
  const articleLocale = ARTICLE_LOCALE_MAP[language] ?? 'en-us';
  useEffect(() => {
    if (labels) {
      axios
        .get(`${API_URL}/help_center/articles/search.json`, {
          params: {
            locale: articleLocale,
            label_names: labels.join(','),
          },
        })
        .then(({ status, data }) => {
          if (status === 200) {
            setStatus({ loading: false });
            const articles = data.results as HelpCenterArticle[];
            articles.forEach((article) => {
              // eslint-disable-next-line no-param-reassign
              article.score =
                article.label_names.reduce(
                  (score, label) => (score + 1) << (labels.indexOf(label) + 1),
                  0,
                ) - article.position;
            });
            setArticles(articles.sort((a, b) => b.score - a.score));
          }
        });
    }
  }, [articleLocale, labels]);
  return [articles, status] as const;
};

export const useHelpCenterUrl = () => {
  const language = useCurrentLanguage();
  const category = getConfigString('zendesk-category');
  if (!category) {
    return null;
  }
  const articleLocale = ARTICLE_LOCALE_MAP[language] ?? 'en-us';
  const googleTranslateQuery =
    articleLocale === 'en-us' && !language.startsWith('en') ? `#googtrans(en-us|${language})` : '';
  return `${BASE_URL}/hc/${articleLocale}/categories/${category}${googleTranslateQuery}`;
};

export const useHelpCenterArticleUrl = (articleId: number) => {
  return `${BASE_URL}/hc/en-us/articles/${articleId}`;
};
